import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import { isLoginEnabled } from "@/api/authSession";
import type { IUserLayout } from "@/api/interfaces/userLayouts";
import type { ResponseFailed } from "@/api/types";
import { getStorage, StorageKey } from "@/constants/storage";
import {
  hasData,
  setStateFailed,
  setStatePending,
} from "@/store/apiRequestStatusMachine";

import { logInWithID } from "./actions";
import type { UserState } from "./type";

const getInitialState = (loadFromLocalStorage: boolean): UserState => {
  const storedData = loadFromLocalStorage ? getStorage(StorageKey.USER) : null;

  return {
    userData: storedData
      ? { status: "stale", state: storedData }
      : { status: "idle" },
    isAuthReady: false,
  };
};

export const userSlice = createSlice({
  name: "user",
  initialState: getInitialState(isLoginEnabled()),
  reducers: {
    setUser: (state, { payload }: PayloadAction<IUserLayout>) => {
      state.userData = { status: "succeeded", state: payload };
      state.isAuthReady = true;
    },
    setGuest: (state) => {
      state.userData = getInitialState(false).userData;
      state.isAuthReady = true;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(logInWithID.pending, (state) => {
        state.isAuthReady = false;
        setStatePending<IUserLayout, ResponseFailed | undefined>(
          state.userData,
          (nextState) => {
            state.userData = nextState;
          },
        );
      })
      .addCase(logInWithID.fulfilled, (state, { payload }) => {
        state.userData = { status: "succeeded", state: payload };
        state.isAuthReady = true;
      })
      .addCase(logInWithID.rejected, (state, { payload }) => {
        setStateFailed<IUserLayout, ResponseFailed | undefined>(
          state.userData,
          (nextState) => {
            state.userData = { ...nextState, state: payload };
          },
        );
        state.isAuthReady = true;
      });
  },
  selectors: {
    selectUserGuid: (state) =>
      hasData(state.userData) ? state.userData.state.GUID : undefined,
    selectUserId: (state) =>
      hasData(state.userData) ? state.userData.state.ID : undefined,
    selectUserData: (state) =>
      hasData(state.userData) ? state.userData.state : undefined,
    selectUserState: (state) => state.userData,
    selectHasUserData: (state) => hasData(state.userData),
    selectIsAuthReady: (state) => state.isAuthReady,
  },
});

export const { setUser, setGuest } = userSlice.actions;

export const {
  selectUserGuid,
  selectUserId,
  selectHasUserData,
  selectUserData,
  selectUserState,
  selectIsAuthReady,
} = userSlice.selectors;

export default userSlice.reducer;
